/**
 * Comprehensive emoji dataset organized by category with search keywords.
 * Used by IconPicker's Emoji tab.
 */

export interface EmojiEntry {
  emoji: string
  name: string
}

export interface EmojiGroup {
  name: string
  icon: string
  items: EmojiEntry[]
}

// Helper to turn compact tuples into EmojiEntry[]
const e = (tuples: [string, string][]): EmojiEntry[] =>
  tuples.map(([emoji, name]) => ({ emoji, name }))

export const EMOJI_GROUPS: EmojiGroup[] = [
  {
    name: "Popular",
    icon: "⭐",
    items: e([
      ["📋", "clipboard"], ["📁", "folder"], ["📂", "open folder"], ["📌", "pushpin"],
      ["📎", "paperclip"], ["✏️", "pencil"], ["📝", "memo note"], ["📖", "book"],
      ["💡", "bulb idea"], ["🎯", "target goal"], ["🚀", "rocket launch"], ["⭐", "star"],
      ["❤️", "heart love"], ["🔥", "fire hot"], ["💰", "money bag"], ["🏠", "house home"],
      ["🎨", "art palette"], ["🎵", "music note"], ["📷", "camera photo"], ["🎬", "movie film"],
      ["🏋️", "gym fitness"], ["🌱", "seedling grow"], ["☕", "coffee"], ["🍕", "pizza"],
      ["✈️", "airplane travel"], ["🚗", "car drive"], ["🎁", "gift present"], ["🎉", "party celebrate"],
      ["📞", "phone call"], ["💻", "laptop computer"], ["🔧", "wrench tool"], ["⚙️", "gear settings"],
      ["🧹", "broom clean"], ["🛒", "cart shopping"], ["📊", "chart bar"], ["📈", "chart up trending"],
      ["🗂️", "dividers files"], ["📆", "calendar date"], ["🔔", "bell notification"], ["🏢", "office building"],
    ]),
  },
  {
    name: "Smileys",
    icon: "😀",
    items: e([
      ["😀", "grin happy"], ["😃", "smiley"], ["😄", "smile"], ["😁", "beam"],
      ["😆", "laughing"], ["😅", "sweat smile"], ["🤣", "rofl"], ["😂", "joy tears"],
      ["🙂", "slight smile"], ["😊", "blush"], ["😇", "angel innocent"], ["🥰", "love hearts"],
      ["😍", "heart eyes"], ["🤩", "star struck"], ["😘", "kiss"], ["😋", "yum delicious"],
      ["😛", "tongue"], ["😜", "wink tongue"], ["🤪", "zany crazy"], ["😝", "squint tongue"],
      ["🤑", "money face"], ["🤗", "hug"], ["🤭", "hand over mouth"], ["🤫", "shush quiet"],
      ["🤔", "thinking"], ["🤐", "zipper mouth"], ["🤨", "raised eyebrow"], ["😐", "neutral"],
      ["😑", "expressionless"], ["😏", "smirk"], ["😒", "unamused"], ["🙄", "rolling eyes"],
      ["😬", "grimace"], ["😌", "relieved"], ["😔", "pensive sad"], ["😪", "sleepy"],
      ["🤤", "drooling"], ["😴", "sleeping zzz"], ["😷", "mask sick"], ["🤒", "thermometer sick"],
      ["🤕", "bandage hurt"], ["🤢", "nauseated"], ["🤮", "vomit"], ["🥵", "hot"],
      ["🥶", "cold frozen"], ["🥴", "dizzy woozy"], ["🤯", "mind blown exploding"], ["🤠", "cowboy"],
      ["🥳", "party celebrate"], ["🥸", "disguise"], ["😎", "cool sunglasses"], ["🤓", "nerd"],
      ["🧐", "monocle"], ["😕", "confused"], ["😟", "worried"], ["😮", "open mouth"],
      ["😲", "astonished"], ["😳", "flushed"], ["🥺", "pleading"], ["😢", "crying"],
      ["😭", "sobbing"], ["😱", "scream fear"], ["😰", "anxious sweat"], ["😡", "rage angry"],
      ["💀", "skull dead"], ["👻", "ghost"], ["👽", "alien"], ["🤖", "robot"],
      ["🤡", "clown"], ["😈", "devil imp"],
    ]),
  },
  {
    name: "People",
    icon: "👋",
    items: e([
      ["👋", "wave hello"], ["🤚", "raised back"], ["🖐️", "hand fingers"], ["✋", "high five"],
      ["🖖", "vulcan spock"], ["👌", "ok perfect"], ["🤌", "pinched italian"], ["🤏", "pinch small"],
      ["✌️", "peace victory"], ["🤞", "fingers crossed luck"], ["🤟", "love you"], ["🤘", "rock metal"],
      ["🤙", "call me"], ["👈", "point left"], ["👉", "point right"], ["👆", "point up"],
      ["👇", "point down"], ["☝️", "index up"], ["👍", "thumbs up like"], ["👎", "thumbs down"],
      ["✊", "fist raised"], ["👊", "fist bump punch"], ["👏", "clap applause"], ["🙌", "raised hands"],
      ["🫶", "heart hands"], ["👐", "open hands"], ["🤲", "palms up"], ["🤝", "handshake deal"],
      ["🙏", "pray please thanks"], ["💪", "muscle strong"], ["🧠", "brain smart"], ["👀", "eyes look"],
      ["👁️", "eye see"], ["👄", "mouth lips"], ["👶", "baby"], ["🧒", "child kid"],
      ["👩", "woman female"], ["👨", "man male"], ["👵", "old woman grandma"], ["👴", "old man grandpa"],
      ["🥷", "ninja stealth"], ["🦸", "superhero"], ["🧙", "mage wizard"], ["🧚", "fairy"],
      ["💃", "dancer salsa"], ["🕺", "disco dance"], ["👫", "couple"], ["👪", "family"],
    ]),
  },
  {
    name: "Animals",
    icon: "🐱",
    items: e([
      ["🐶", "dog puppy"], ["🐱", "cat kitty"], ["🐭", "mouse"], ["🐹", "hamster"],
      ["🐰", "rabbit bunny"], ["🦊", "fox"], ["🐻", "bear"], ["🐼", "panda"],
      ["🐨", "koala"], ["🐯", "tiger"], ["🦁", "lion king"], ["🐮", "cow"],
      ["🐷", "pig"], ["🐸", "frog"], ["🐵", "monkey"], ["🐔", "chicken"],
      ["🐧", "penguin"], ["🐦", "bird"], ["🦅", "eagle"], ["🦉", "owl wise"],
      ["🦇", "bat"], ["🐺", "wolf"], ["🐴", "horse"], ["🦄", "unicorn magic"],
      ["🐝", "bee honey"], ["🐛", "bug caterpillar"], ["🦋", "butterfly"], ["🐌", "snail slow"],
      ["🐙", "octopus"], ["🐳", "whale"], ["🐬", "dolphin"], ["🐟", "fish"],
      ["🦈", "shark"], ["🐢", "turtle slow"], ["🐍", "snake"], ["🦕", "dinosaur"],
      ["🦀", "crab"], ["🦐", "shrimp"], ["🌸", "blossom flower"], ["🌹", "rose"],
      ["🌻", "sunflower"], ["🌱", "seedling grow plant"], ["🌳", "tree"], ["🌴", "palm tropical"],
      ["🌵", "cactus desert"], ["🍄", "mushroom"], ["🍃", "leaf wind"], ["🍂", "fallen leaf autumn"],
      ["🍀", "four leaf clover luck"], ["🌿", "herb"], ["🪴", "potted plant"],
    ]),
  },
  {
    name: "Food",
    icon: "🍎",
    items: e([
      ["🍎", "apple red"], ["🍏", "apple green"], ["🍐", "pear"], ["🍊", "orange tangerine"],
      ["🍋", "lemon"], ["🍌", "banana"], ["🍉", "watermelon"], ["🍇", "grapes"],
      ["🍓", "strawberry"], ["🫐", "blueberry"], ["🍒", "cherry"], ["🍑", "peach"],
      ["🥭", "mango"], ["🍍", "pineapple"], ["🥥", "coconut"], ["🥑", "avocado"],
      ["🍅", "tomato"], ["🥕", "carrot"], ["🌽", "corn"], ["🌶️", "pepper hot chili"],
      ["🥦", "broccoli"], ["🧅", "onion"], ["🧄", "garlic"], ["🍞", "bread"],
      ["🥐", "croissant"], ["🍕", "pizza"], ["🍔", "hamburger burger"], ["🍟", "fries"],
      ["🌭", "hotdog"], ["🌮", "taco"], ["🌯", "burrito"], ["🍣", "sushi"],
      ["🍜", "ramen noodles"], ["🍚", "rice"], ["🍛", "curry"], ["🥗", "salad"],
      ["🎂", "cake birthday"], ["🥧", "pie"], ["🍩", "donut"], ["🍪", "cookie"],
      ["🍫", "chocolate"], ["🍬", "candy sweet"], ["🍭", "lollipop"], ["🍦", "ice cream"],
      ["☕", "coffee"], ["🍵", "tea"], ["🍺", "beer"], ["🍷", "wine"],
      ["🍸", "cocktail martini"], ["🧃", "juice box"], ["🥛", "milk"], ["💧", "water drop"],
    ]),
  },
  {
    name: "Travel",
    icon: "✈️",
    items: e([
      ["🚗", "car automobile"], ["🚕", "taxi cab"], ["🚌", "bus"], ["🚑", "ambulance"],
      ["🚒", "fire truck engine"], ["🚓", "police car"], ["🚛", "truck delivery"], ["🏎️", "racing car"],
      ["🏍️", "motorcycle"], ["🚲", "bicycle bike"], ["🛵", "scooter"], ["🛴", "kick scooter"],
      ["✈️", "airplane flight"], ["🚀", "rocket space"], ["🚢", "ship cruise"], ["⛵", "sailboat"],
      ["🚆", "train"], ["🚇", "metro subway"], ["🚂", "locomotive steam"], ["🛸", "ufo flying saucer"],
      ["🏠", "house home"], ["🏡", "house garden"], ["🏢", "office building"], ["🏛️", "classical building"],
      ["🏥", "hospital"], ["🏫", "school"], ["🏦", "bank"], ["🏨", "hotel"],
      ["⛪", "church"], ["🏰", "castle"], ["🏗️", "construction crane"], ["⛺", "tent camping"],
      ["⛰️", "mountain"], ["🌋", "volcano"], ["🗻", "mount fuji"], ["🏔️", "snow mountain"],
      ["🌍", "globe earth"], ["🗺️", "world map"], ["🧭", "compass direction"], ["🏖️", "beach"],
      ["🏝️", "island desert"], ["🌅", "sunset"], ["🌄", "sunrise"], ["🌈", "rainbow"],
      ["⭐", "star"], ["🌙", "moon crescent"], ["☀️", "sun"], ["☁️", "cloud"],
      ["🌧️", "rain"], ["❄️", "snow cold"], ["⚡", "lightning bolt"], ["🌪️", "tornado"],
      ["☂️", "umbrella"], ["🔥", "fire flame"], ["✨", "sparkles magic"],
    ]),
  },
  {
    name: "Activities",
    icon: "⚽",
    items: e([
      ["⚽", "soccer football"], ["🏀", "basketball"], ["🏈", "football american"], ["⚾", "baseball"],
      ["🎾", "tennis"], ["🏐", "volleyball"], ["🏉", "rugby"], ["🏓", "ping pong"],
      ["🏸", "badminton"], ["🏒", "hockey"], ["⛳", "golf"], ["🏹", "archery bow"],
      ["🎣", "fishing"], ["⛷️", "skiing"], ["🏊", "swimming"], ["🏄", "surfing"],
      ["🧗", "climbing"], ["🏃", "running"], ["🚶", "walking"], ["🚴", "cycling bike"],
      ["🧘", "yoga meditation"], ["🥋", "martial arts karate"], ["🥊", "boxing gloves"],
      ["🏆", "trophy winner"], ["🥇", "gold medal first"], ["🥈", "silver medal second"], ["🥉", "bronze medal third"],
      ["🎯", "target bullseye goal"], ["🎮", "game controller"], ["🕹️", "joystick arcade"], ["🎲", "dice game"],
      ["🧩", "puzzle piece"], ["♟️", "chess"], ["🎳", "bowling"], ["🎨", "art palette paint"],
      ["🎵", "music note"], ["🎶", "music notes"], ["🎸", "guitar"], ["🥁", "drum"],
      ["🎹", "piano keyboard"], ["🎬", "movie clapperboard"], ["📷", "camera photo"], ["📹", "video camera"],
      ["🎤", "microphone sing"], ["🎧", "headphones"], ["🎫", "ticket event"], ["🎪", "circus tent"],
    ]),
  },
  {
    name: "Objects",
    icon: "💡",
    items: e([
      ["📱", "phone mobile"], ["💻", "laptop computer"], ["🖥️", "desktop screen"], ["⌨️", "keyboard"],
      ["🖱️", "mouse computer"], ["🖨️", "printer"], ["📸", "camera flash"], ["🔦", "flashlight torch"],
      ["🔋", "battery"], ["🔌", "plug electric"], ["💡", "bulb idea light"], ["🔍", "magnifying search"],
      ["🔬", "microscope science"], ["🔭", "telescope astronomy"], ["⌚", "watch time"], ["🕐", "clock one"],
      ["⏰", "alarm clock"], ["⏳", "hourglass timer"], ["📅", "calendar date"], ["📋", "clipboard list"],
      ["📌", "pushpin pin"], ["📎", "paperclip attach"], ["🖊️", "pen"], ["✏️", "pencil write"],
      ["🖍️", "crayon"], ["🖌️", "paintbrush art"], ["📓", "notebook"], ["📔", "notebook decorated"],
      ["📖", "book open"], ["📚", "books stack"], ["📁", "folder"], ["📂", "open folder"],
      ["📄", "document page"], ["📰", "newspaper"], ["📧", "email mail"], ["✉️", "envelope letter"],
      ["📦", "package box"], ["🏷️", "tag label"], ["🔖", "bookmark"], ["💰", "money bag"],
      ["🪙", "coin"], ["💳", "credit card payment"], ["💎", "gem diamond"], ["⚖️", "scale balance"],
      ["🔧", "wrench tool"], ["🔨", "hammer build"], ["🪛", "screwdriver"], ["⚙️", "gear settings cog"],
      ["🔗", "link chain url"], ["🔒", "lock locked secure"], ["🔓", "unlock open"], ["🔑", "key access"],
      ["🛡️", "shield protect security"], ["💣", "bomb"], ["💊", "medicine pill"], ["🩹", "bandage"],
      ["🧬", "dna genetics"], ["🧪", "test tube science"], ["🧹", "broom clean sweep"], ["🧺", "basket laundry"],
      ["🎁", "gift present"], ["🎈", "balloon party"], ["🎀", "ribbon bow"], ["🎉", "party confetti"],
      ["🔔", "bell notification alert"], ["🔕", "bell muted silent"], ["📣", "megaphone announce"],
      ["🏁", "checkered flag finish"], ["🚩", "flag red"],
    ]),
  },
  {
    name: "Symbols",
    icon: "💯",
    items: e([
      ["❤️", "heart red love"], ["🧡", "orange heart"], ["💛", "yellow heart"], ["💚", "green heart"],
      ["💙", "blue heart"], ["💜", "purple heart"], ["🖤", "black heart"], ["🤍", "white heart"],
      ["🩷", "pink heart"], ["💔", "broken heart"], ["❤️‍🔥", "fire heart passion"], ["💯", "hundred perfect"],
      ["✅", "check mark done"], ["❌", "cross wrong"], ["❗", "exclamation warning"], ["❓", "question"],
      ["‼️", "double exclamation"], ["⁉️", "exclamation question"], ["➕", "plus add"], ["➖", "minus subtract"],
      ["✖️", "multiply cross"], ["➗", "divide"], ["♾️", "infinity"], ["♻️", "recycle green"],
      ["⚠️", "warning caution"], ["🚫", "no entry prohibited"], ["⛔", "stop prohibited"], ["☮️", "peace"],
      ["☯️", "yin yang balance"], ["⚛️", "atom science"], ["🔴", "red circle"], ["🟠", "orange circle"],
      ["🟡", "yellow circle"], ["🟢", "green circle"], ["🔵", "blue circle"], ["🟣", "purple circle"],
      ["⚫", "black circle"], ["⚪", "white circle"], ["🟤", "brown circle"], ["🔺", "red triangle up"],
      ["🔻", "red triangle down"], ["🔶", "orange diamond large"], ["🔷", "blue diamond large"],
      ["🔸", "orange diamond small"], ["🔹", "blue diamond small"],
      ["▪️", "black square small"], ["▫️", "white square small"], ["◼️", "black square medium"],
      ["◻️", "white square medium"], ["⬛", "black square large"], ["⬜", "white square large"],
      ["➡️", "right arrow"], ["⬅️", "left arrow"], ["⬆️", "up arrow"], ["⬇️", "down arrow"],
      ["↗️", "up right arrow"], ["↘️", "down right arrow"], ["↙️", "down left arrow"], ["↖️", "up left arrow"],
      ["🔄", "refresh reload"], ["🔀", "shuffle random"], ["🔁", "repeat loop"], ["🔂", "repeat one"],
      ["▶️", "play"], ["⏸️", "pause"], ["⏹️", "stop"], ["⏭️", "next forward"],
      ["⏮️", "previous back"], ["🔊", "speaker volume loud"], ["🔇", "speaker muted"], ["🔈", "speaker low"],
      ["💤", "zzz sleep"], ["💬", "speech bubble chat"], ["💭", "thought bubble"], ["🗨️", "speech left"],
    ]),
  },
  {
    name: "Flags",
    icon: "🏁",
    items: e([
      ["🏳️", "white flag surrender"], ["🏴", "black flag"], ["🏁", "checkered flag race finish"],
      ["🚩", "red flag triangular"], ["🏳️‍🌈", "rainbow flag pride"], ["🏴‍☠️", "pirate flag jolly roger"],
      ["🇺🇸", "us usa america"], ["🇬🇧", "uk united kingdom britain"], ["🇫🇷", "france french"],
      ["🇩🇪", "germany german"], ["🇮🇹", "italy italian"], ["🇪🇸", "spain spanish"],
      ["🇯🇵", "japan japanese"], ["🇰🇷", "korea south korean"], ["🇨🇳", "china chinese"],
      ["🇮🇳", "india indian"], ["🇧🇷", "brazil brazilian"], ["🇦🇺", "australia australian"],
      ["🇨🇦", "canada canadian"], ["🇲🇽", "mexico mexican"], ["🇷🇺", "russia russian"],
      ["🇸🇪", "sweden swedish"], ["🇳🇴", "norway norwegian"], ["🇩🇰", "denmark danish"],
      ["🇫🇮", "finland finnish"], ["🇮🇸", "iceland icelandic"], ["🇳🇱", "netherlands dutch"],
      ["🇨🇭", "switzerland swiss"], ["🇦🇹", "austria austrian"], ["🇧🇪", "belgium belgian"],
      ["🇵🇹", "portugal portuguese"], ["🇵🇱", "poland polish"], ["🇮🇪", "ireland irish"],
      ["🇬🇷", "greece greek"], ["🇹🇷", "turkey turkish"], ["🇿🇦", "south africa african"],
      ["🇦🇷", "argentina argentinian"], ["🇨🇴", "colombia colombian"],
      ["🇹🇭", "thailand thai"], ["🇻🇳", "vietnam vietnamese"],
      ["🇮🇩", "indonesia indonesian"], ["🇵🇭", "philippines filipino"],
      ["🇸🇬", "singapore"], ["🇲🇾", "malaysia malaysian"],
      ["🇪🇬", "egypt egyptian"], ["🇳🇬", "nigeria nigerian"],
      ["🇰🇪", "kenya kenyan"], ["🇺🇦", "ukraine ukrainian"],
      ["🇪🇺", "eu european union"],
    ]),
  },
]
